import { Button, CircularProgress, Divider, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import '../css/modal.css'

// Excel columns, in template order. `key` is the ProductsDao field; `num`
// marks numeric columns (empty cells become 0).
const cols = [
    { head: 'Баркод*', key: 'prod_code' },
    { head: 'UPC код', key: 'prod_upc_code' },
    { head: 'Material код', key: 'prod_mat_id' },
    { head: 'Компани*', key: 'prod_comp_id' },
    { head: 'Бүтээгдэхүүний нэр*', key: 'prod_name' },
    { head: 'Хэмжээ', key: 'prod_size', num: true },
    { head: 'Нэгж KG/L', key: 'prod_unit' },
    { head: 'Брэнд', key: 'prod_brand_id' },
    { head: 'Хайрцаг дахь тоо', key: 'prod_num_in_box', num: true, int: true },
    { head: 'Тайлбар', key: 'prod_desc' },
    { head: 'Үнэ', key: 'prod_price', num: true },
    { head: 'Үлдэгдэл', key: 'prod_uldegdel', num: true, int: true },
    { head: 'Тасарсан эсэх', key: 'prod_tasarsan_eseh' },
    { head: 'Урамшуулал хувь', key: 'prod_bonus_prcnt', num: true },
    { head: 'Урамшуулал тоо (дээш)', key: 'prod_bonus_unit', num: true, int: true },
];

const api = 'https://wms-cloud.com/api';
//const api = 'http://localhost:8080';

const norm = (v) => String(v == null ? '' : v).trim().toLowerCase();

const ProductImport = ({ token, handleClose, handleRefresh }) => {
    const [loading, setLoad] = useState(false);
    const [companies, setCompanies] = useState([]);
    const [brands, setBrands] = useState([]);
    const [fileName, setFileName] = useState("");
    const [rows, setRows] = useState([]);
    const fileref = useRef();

    const handleEscape = (e) => {
        if (e.keyCode == 27) {
            handleClose(false);
        }
    }

    useEffect(() => {
        window.addEventListener('keyup', handleEscape);
        return () => {
            window.removeEventListener('keyup', handleEscape);
        }
    }, [handleEscape])

    useEffect(() => {
        const headers = new Headers({ 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` });
        fetch(new Request(api + '/Company', { method: 'GET', headers })).then((r) => r.status == 200 ? r.json() : []).then((r) => setCompanies(r || []));
        fetch(new Request(api + '/Brand?_start=0&_end=5000', { method: 'GET', headers })).then((r) => r.status == 200 ? r.json() : []).then((r) => setBrands(r || []));
    }, [])

    const downloadTemplate = () => {
        const example = ['8650000000001', '', '', companies.length ? companies[0].com_id : 'SUP1', 'Жишээ бүтээгдэхүүн', 1, 'KG', brands.length ? brands[0].brand_name : '', 12, '', 15000, 100, '', 0, 0];
        const ws = XLSX.utils.aoa_to_sheet([cols.map((c) => c.head), example]);
        ws['!cols'] = cols.map(() => ({ wch: 18 }));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Бараа');
        XLSX.writeFile(wb, 'baraa_import.xlsx');
    }

    // Company may be given as code or name, brand as name or id.
    const findCompany = (v) => {
        const c = companies.find((e) => norm(e.com_id) === norm(v)) || companies.find((e) => norm(e.com_name) === norm(v));
        return c ? String(c.com_id) : null;
    }
    const findBrand = (v) => {
        const b = brands.find((e) => norm(e.brand_name) === norm(v)) || brands.find((e) => String(e.id) === String(v).trim());
        return b ? b.id : null;
    }

    const toRow = (raw, i) => {
        const prod = {};
        const errors = [];
        cols.forEach((c) => {
            const v = raw[c.head] == null ? '' : raw[c.head];
            if (c.num) {
                if (String(v).trim() === '') {
                    prod[c.key] = 0;
                } else {
                    const n = Number(String(v).replace(/,/g, ''));
                    if (isNaN(n)) errors.push(c.head + ' тоо биш');
                    prod[c.key] = isNaN(n) ? 0 : (c.int ? Math.trunc(n) : n);
                }
            } else {
                // Integers print without exponent up to 1e21, which covers barcodes.
                prod[c.key] = (typeof v === 'number' ? v.toLocaleString('fullwide', { useGrouping: false, maximumFractionDigits: 20 }) : String(v)).trim();
            }
        });
        if (prod.prod_code === '') errors.push('Баркод хоосон');
        if (prod.prod_name === '') errors.push('Нэр хоосон');
        const comp = findCompany(prod.prod_comp_id);
        if (comp == null) errors.push('Компани олдсонгүй');
        else prod.prod_comp_id = comp;
        if (prod.prod_brand_id === '') {
            prod.prod_brand_id = 0;
        } else {
            const b = findBrand(prod.prod_brand_id);
            if (b == null) errors.push('Брэнд олдсонгүй');
            prod.prod_brand_id = b == null ? 0 : b;
        }
        // Excel line number: header is line 1.
        return { line: i + 2, prod, raw, errors };
    }

    const handleFile = (e) => {
        const file = e.target.files[0];
        e.target.value = '';
        if (!file) return;
        setFileName(file.name);
        const reader = new FileReader();
        reader.onload = (ev) => {
            try {
                const wb = XLSX.read(ev.target.result, { type: 'array' });
                const ws = wb.Sheets[wb.SheetNames[0]];
                // Raw cell values: formatted text would turn a 13-digit barcode
                // into "8.65E+12". Numbers are turned back into text in toRow.
                const data = XLSX.utils.sheet_to_json(ws, { defval: '', raw: true })
                    .map((r) => Object.fromEntries(Object.entries(r).map(([k, v]) => [k.trim(), v])))
                    .filter((r) => Object.values(r).some((v) => String(v).trim() !== ''));
                const missing = cols.filter((c) => c.head.endsWith('*')).filter((c) => data.length && !(c.head in data[0]));
                if (missing.length) {
                    alert("Багана олдсонгүй: " + missing.map((c) => c.head).join(', ') + ". Загвар файлыг ашиглана уу.");
                    setRows([]);
                    return;
                }
                setRows(data.map(toRow));
            } catch (err) {
                alert("Excel файл уншиж чадсангүй.");
                setRows([]);
            }
        };
        reader.readAsArrayBuffer(file);
    }

    const errorCount = rows.filter((r) => r.errors.length).length;

    const handleSubmit = () => {
        if (rows.length == 0 || errorCount > 0) return;
        setLoad(true);
        const request = new Request(api + '/Products/Import', {
            method: 'POST',
            body: JSON.stringify(rows.map((r) => r.prod)),
            headers: new Headers({ 'Content-Type': 'application/json', 'Accept': 'application/json', 'Authorization': `Bearer ${token}` })
        });
        fetch(request).then((response) => {
            if (response.status == 200 || response.status == 400) {
                return response.json().then((body) => ({ status: response.status, body }));
            }
            return { status: response.status, body: {} };
        }).then(({ status, body }) => {
            setLoad(false);
            if (status == 200) {
                alert(body.inserted + " бараа амжилттай нэмэгдлээ.");
                handleClose(false);
                handleRefresh();
            } else if (body.errors) {
                // Server-side checks (barcode already in DB, etc.) shown per row.
                setRows(rows.map((r, i) => {
                    const errs = body.errors.filter((e) => e.index === i).map((e) => e.message);
                    return errs.length ? { ...r, errors: [...r.errors, ...errs] } : r;
                }));
                alert("Алдаатай мөр байна. Юу ч хадгалагдаагүй.");
            } else {
                alert("Амжилтгүй " + status + (body.message ? ": " + body.message : ""));
            }
        }).catch(() => {
            setLoad(false);
            alert("Алдаа гарлаа дахин оролдоно уу.");
        });
    }

    return <div className="custmodal">
        <div className="mcont" style={{ maxWidth: "1100px", width: "95vw" }}>
            <div className="mheader">
                Excel-ээр бараа нэмэх
            <button aria-label="Хаах" onClick={() => handleClose(false)}>✕</button>
            </div>
            <Divider />
            <div className="mccont">
                <div className="crow" style={{ alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                    <Button variant="outlined" onClick={downloadTemplate}>Загвар татах</Button>
                    <Button variant="contained" onClick={() => fileref.current.click()}>Excel файл сонгох</Button>
                    <input ref={fileref} type="file" accept=".xlsx,.xls,.csv" style={{ display: "none" }} onChange={handleFile} />
                    <span>{fileName}</span>
                </div>
                <div className="crow">
                    <div style={{ width: "100%", fontSize: "13px", opacity: 0.8 }}>
                        * тэмдэгтэй багана заавал. Компанийг код эсвэл нэрээр, брэндийг нэрээр бичнэ. Баркод бүртгэлтэй бол тухайн мөр алдаа болно. Алдаатай мөр байвал юу ч хадгалагдахгүй.
                    </div>
                </div>
                {rows.length ?
                    <div className="crow">
                        <div style={{ width: "100%" }}>
                            <div style={{ marginBottom: "6px" }}>
                                Нийт {rows.length} мөр{errorCount ? <span style={{ color: "#d32f2f" }}>, {errorCount} алдаатай</span> : ", алдаагүй"}
                            </div>
                            <TableContainer style={{ maxHeight: "45vh" }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Мөр</TableCell>
                                            <TableCell>Баркод</TableCell>
                                            <TableCell>Нэр</TableCell>
                                            <TableCell>Компани</TableCell>
                                            <TableCell>Брэнд</TableCell>
                                            <TableCell>Үнэ</TableCell>
                                            <TableCell>Үлдэгдэл</TableCell>
                                            <TableCell>Төлөв</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {rows.map((r) => (
                                            <TableRow key={r.line} style={r.errors.length ? { background: "rgba(211,47,47,0.08)" } : {}}>
                                                <TableCell>{r.line}</TableCell>
                                                <TableCell>{r.prod.prod_code}</TableCell>
                                                <TableCell>{r.prod.prod_name}</TableCell>
                                                <TableCell>{r.raw['Компани*']}</TableCell>
                                                <TableCell>{r.raw['Брэнд']}</TableCell>
                                                <TableCell>{r.prod.prod_price}</TableCell>
                                                <TableCell>{r.prod.prod_uldegdel}</TableCell>
                                                <TableCell style={{ color: r.errors.length ? "#d32f2f" : "#2e7d32" }}>
                                                    {r.errors.length ? r.errors.join(', ') : 'OK'}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </div>
                    </div> : <></>
                }
            </div>
            <div className="mfooter">
                <Button onClick={() => handleClose(false)}>Буцах</Button>
                <Button variant="contained" disabled={rows.length == 0 || errorCount > 0} onClick={handleSubmit}>
                    {rows.length ? rows.length + " бараа хадгалах" : "Хадгалах"}
                </Button>
            </div>
            {loading ?
                <div className="prodload">
                    <CircularProgress />
                </div> : ""
            }
        </div>
    </div>
}

export default ProductImport;
